import { projects } from '../data/projects';
import { writingItems, bookDetails } from '../data/writing';
import { bookshelf } from '../data/bookshelf';

const BASE_URL = 'https://girishgoyal.netlify.app';
const DEFAULT_IMAGE = `${BASE_URL}/profile.jpg`;
const TWITTER_HANDLE = '@GirishkumarGoy5';

export const getSeoMetadata = (pathname) => {
  const cleanPath = pathname.replace(/\/$/, '') || '/';

  // 1. Dynamic Project Detail Pages (/work/:slug)
  if (cleanPath.startsWith('/work/') && cleanPath !== '/work/building' && cleanPath !== '/work/archive') {
    const slug = cleanPath.replace('/work/', '');
    const project = projects.find((p) => p.slug === slug || p.id === slug);
    if (project) {
      const isBook = slug.includes('handbook') || slug.includes('book');
      return {
        title: `${project.title} | Girish Kumar Goyal`,
        description: project.shortDescription || (project.longDescription ? project.longDescription.slice(0, 155) : `Engineering case study and technical architecture of ${project.title}.`),
        canonical: `${BASE_URL}/work/${project.slug}`,
        ogType: isBook ? 'book' : 'article',
        ogImage: DEFAULT_IMAGE,
        robots: 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1',
        breadcrumbs: [
          { name: 'Home', url: `${BASE_URL}/` },
          { name: 'Selected Work', url: `${BASE_URL}/work` },
          { name: project.title, url: `${BASE_URL}/work/${project.slug}` }
        ],
        schema: isBook
          ? {
              "@context": "https://schema.org",
              "@type": "Book",
              "name": project.title,
              "author": { "@type": "Person", "name": "Girish Kumar Goyal" },
              "description": project.shortDescription,
              "inLanguage": "en",
              "genre": project.category,
              "url": `${BASE_URL}/work/${project.slug}`
            }
          : {
              "@context": "https://schema.org",
              "@type": "SoftwareSourceCode",
              "name": project.title,
              "description": project.shortDescription,
              "author": { "@type": "Person", "name": "Girish Kumar Goyal" },
              "programmingLanguage": project.technologies?.join(', '),
              "url": `${BASE_URL}/work/${project.slug}`
            }
      };
    }
  }

  // 2. Dynamic Writing Detail Pages (/writing/:slug)
  if (cleanPath.startsWith('/writing/') && cleanPath !== '/writing/book') {
    const slug = cleanPath.replace('/writing/', '');
    const writing = writingItems.find((w) => w.slug === slug || w.id === slug);
    if (writing) {
      return {
        title: `${writing.title} | Girish Kumar Goyal`,
        description: writing.summary || `Technical essay by Girish Kumar Goyal exploring ${writing.title}.`,
        canonical: `${BASE_URL}/writing/${writing.slug}`,
        ogType: 'article',
        ogImage: DEFAULT_IMAGE,
        robots: 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1',
        breadcrumbs: [
          { name: 'Home', url: `${BASE_URL}/` },
          { name: 'Writing', url: `${BASE_URL}/writing` },
          { name: writing.title, url: `${BASE_URL}/writing/${writing.slug}` }
        ],
        schema: {
          "@context": "https://schema.org",
          "@type": "TechArticle",
          "headline": writing.title,
          "description": writing.summary,
          "author": { "@type": "Person", "name": "Girish Kumar Goyal", "url": `${BASE_URL}/about` },
          "publisher": { "@type": "Person", "name": "Girish Kumar Goyal" },
          "datePublished": `${writing.date || '2024'}-01-01`,
          "url": `${BASE_URL}/writing/${writing.slug}`
        }
      };
    }
  }

  // 3. Dynamic Book Detail Pages (/reading/:slug)
  if (cleanPath.startsWith('/reading/') && cleanPath !== '/reading/bookshelf' && cleanPath !== '/reading/history') {
    const slug = cleanPath.replace('/reading/', '');
    const book = bookshelf.find((b) => b.slug === slug || b.id === slug);
    if (book) {
      return {
        title: `${book.title} by ${book.author} | Digital Library`,
        description: book.description ? book.description.slice(0, 155) : `Reading review and notes on ${book.title} by ${book.author}.`,
        canonical: `${BASE_URL}/reading/${book.slug}`,
        ogType: 'book',
        ogImage: DEFAULT_IMAGE,
        robots: 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1',
        breadcrumbs: [
          { name: 'Home', url: `${BASE_URL}/` },
          { name: 'Reading', url: `${BASE_URL}/reading` },
          { name: 'Bookshelf', url: `${BASE_URL}/reading/bookshelf` },
          { name: book.title, url: `${BASE_URL}/reading/${book.slug}` }
        ],
        schema: {
          "@context": "https://schema.org",
          "@type": "Book",
          "name": book.title,
          "author": { "@type": "Person", "name": book.author },
          "description": book.description,
          "genre": book.category,
          "inLanguage": book.language || "en",
          "numberOfPages": book.pages,
          "url": `${BASE_URL}/reading/${book.slug}`
        }
      };
    }
  }

  // 4. Static Canonical Routes
  switch (cleanPath) {
    case '/':
      return {
        title: 'Girish Kumar Goyal — Software Engineer · Systems & Algorithms',
        description: 'Portfolio of Girish Kumar Goyal. Software Engineer specializing in Systems Architecture, C/C++, Competitive Programming (Codeforces 2013), and Full-Stack Engineering.',
        canonical: `${BASE_URL}/`,
        ogType: 'website',
        ogImage: DEFAULT_IMAGE,
        robots: 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1',
        breadcrumbs: [{ name: 'Home', url: `${BASE_URL}/` }]
      };

    case '/about':
      return {
        title: 'About Girish Kumar Goyal | Background, Principles & Engineering',
        description: 'Software engineer with a background in Computer Science & Engineering, algorithms, systems programming, and writing. Based in India.',
        canonical: `${BASE_URL}/about`,
        ogType: 'profile',
        ogImage: DEFAULT_IMAGE,
        robots: 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1',
        breadcrumbs: [
          { name: 'Home', url: `${BASE_URL}/` },
          { name: 'About', url: `${BASE_URL}/about` }
        ],
        schema: {
          "@context": "https://schema.org",
          "@type": "ProfilePage",
          "name": "About Girish Kumar Goyal",
          "url": `${BASE_URL}/about`,
          "mainEntity": {
            "@type": "Person",
            "name": "Girish Kumar Goyal",
            "jobTitle": "Software Engineer",
            "url": `${BASE_URL}/about`
          }
        }
      };

    case '/work':
      return {
        title: 'Selected Work & Systems | Girish Kumar Goyal',
        description: 'Software systems, algorithms, and developer tools built by Girish Kumar Goyal: POSIX Shell in C, LaTeX DSA Handbook, and Web Terminals.',
        canonical: `${BASE_URL}/work`,
        ogType: 'website',
        ogImage: DEFAULT_IMAGE,
        robots: 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1',
        breadcrumbs: [
          { name: 'Home', url: `${BASE_URL}/` },
          { name: 'Selected Work', url: `${BASE_URL}/work` }
        ]
      };

    case '/work/building':
      return {
        title: 'Currently Building — Active Projects | Girish Kumar Goyal',
        description: 'Active software projects and systems currently under development by Girish Kumar Goyal.',
        canonical: `${BASE_URL}/work/building`,
        ogType: 'website',
        ogImage: DEFAULT_IMAGE,
        robots: 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1',
        breadcrumbs: [
          { name: 'Home', url: `${BASE_URL}/` },
          { name: 'Work', url: `${BASE_URL}/work` },
          { name: 'Currently Building', url: `${BASE_URL}/work/building` }
        ]
      };

    case '/work/archive':
      return {
        title: 'Project Archive — Complete Catalog | Girish Kumar Goyal',
        description: 'Chronological archive of software prototypes, algorithms, and technical projects built over the years.',
        canonical: `${BASE_URL}/work/archive`,
        ogType: 'website',
        ogImage: DEFAULT_IMAGE,
        robots: 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1',
        breadcrumbs: [
          { name: 'Home', url: `${BASE_URL}/` },
          { name: 'Work', url: `${BASE_URL}/work` },
          { name: 'Archive', url: `${BASE_URL}/work/archive` }
        ]
      };

    case '/writing':
      return {
        title: 'Writing & Book Manuscript | Girish Kumar Goyal',
        description: 'Essays on systems thinking, code simplicity, competitive programming intuition, and in-progress book manuscript on biological systems.',
        canonical: `${BASE_URL}/writing`,
        ogType: 'website',
        ogImage: DEFAULT_IMAGE,
        robots: 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1',
        breadcrumbs: [
          { name: 'Home', url: `${BASE_URL}/` },
          { name: 'Writing', url: `${BASE_URL}/writing` }
        ]
      };

    case '/writing/book':
      return {
        title: 'Biology through Curiosity : Questions that became Knowledge | Book Manuscript',
        description: 'In-progress book manuscript by Girish Kumar Goyal exploring how curious questions in biology transformed into structured scientific knowledge.',
        canonical: `${BASE_URL}/writing/book`,
        ogType: 'book',
        ogImage: DEFAULT_IMAGE,
        robots: 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1',
        breadcrumbs: [
          { name: 'Home', url: `${BASE_URL}/` },
          { name: 'Writing', url: `${BASE_URL}/writing` },
          { name: 'Book Manuscript', url: `${BASE_URL}/writing/book` }
        ],
        schema: {
          "@context": "https://schema.org",
          "@type": "Book",
          "name": bookDetails?.title || "Biology through Curiosity : Questions that became Knowledge",
          "author": { "@type": "Person", "name": "Girish Kumar Goyal" },
          "description": bookDetails?.description || "An in-progress book manuscript exploring how curious questions in natural biology transformed into structured scientific knowledge.",
          "inLanguage": "en",
          "url": `${BASE_URL}/writing/book`
        }
      };

    case '/reading':
    case '/reading/bookshelf':
      return {
        title: 'Personal Bookshelf & Digital Library | Girish Kumar Goyal',
        description: 'Curated digital catalogue of physical books spanning history, statecraft, philosophy, classical literature, and computing.',
        canonical: `${BASE_URL}/reading/bookshelf`,
        ogType: 'website',
        ogImage: DEFAULT_IMAGE,
        robots: 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1',
        breadcrumbs: [
          { name: 'Home', url: `${BASE_URL}/` },
          { name: 'Reading', url: `${BASE_URL}/reading` },
          { name: 'Bookshelf', url: `${BASE_URL}/reading/bookshelf` }
        ]
      };

    case '/reading/history':
    case '/history':
      return {
        title: 'History & Civilizational Ideas Timeline | Girish Kumar Goyal',
        description: 'Chronological timeline exploring key historical milestones, institutional evolution, statecraft, and intellectual history.',
        canonical: `${BASE_URL}/history`,
        ogType: 'website',
        ogImage: DEFAULT_IMAGE,
        robots: 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1',
        breadcrumbs: [
          { name: 'Home', url: `${BASE_URL}/` },
          { name: 'History & Ideas', url: `${BASE_URL}/history` }
        ]
      };

    case '/codeforces':
      return {
        title: 'Codeforces Candidate Master (2013 Peak Rating) | Girish Kumar Goyal',
        description: 'Live competitive programming telemetry for Girish Kumar Goyal (epsilon_xd). Verified Candidate Master with 2013 peak rating and 1,000+ solved problems.',
        canonical: `${BASE_URL}/codeforces`,
        ogType: 'profile',
        ogImage: DEFAULT_IMAGE,
        robots: 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1',
        breadcrumbs: [
          { name: 'Home', url: `${BASE_URL}/` },
          { name: 'Codeforces Telemetry', url: `${BASE_URL}/codeforces` }
        ],
        schema: {
          "@context": "https://schema.org",
          "@type": "ProfilePage",
          "name": "Girish Kumar Goyal — Codeforces Candidate Master",
          "url": `${BASE_URL}/codeforces`,
          "mainEntity": {
            "@type": "Person",
            "name": "Girish Kumar Goyal",
            "sameAs": ["https://codeforces.com/profile/epsilon_xd"],
            "award": "Codeforces Candidate Master (Max Rating: 2013)"
          }
        }
      };

    case '/learning':
      return {
        title: 'Structured Learning & Knowledge Map | Girish Kumar Goyal',
        description: 'Systematic study curriculum spanning history, constitutional polity, political economy, and core systems programming.',
        canonical: `${BASE_URL}/learning`,
        ogType: 'website',
        ogImage: DEFAULT_IMAGE,
        robots: 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1',
        breadcrumbs: [
          { name: 'Home', url: `${BASE_URL}/` },
          { name: 'Learning', url: `${BASE_URL}/learning` }
        ]
      };

    case '/journey':
      return {
        title: 'The Journey & Chronological Milestones | Girish Kumar Goyal',
        description: 'Chronological evolution through college education, software development, competitive programming, and systems research.',
        canonical: `${BASE_URL}/journey`,
        ogType: 'website',
        ogImage: DEFAULT_IMAGE,
        robots: 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1',
        breadcrumbs: [
          { name: 'Home', url: `${BASE_URL}/` },
          { name: 'The Journey', url: `${BASE_URL}/journey` }
        ]
      };

    case '/resume':
      return {
        title: 'Curriculum Vitae / Resume | Girish Kumar Goyal',
        description: 'Resume of Girish Kumar Goyal: Education (B.Tech CSE 2022), Codeforces Candidate Master (2013), technical skills, and systems projects.',
        canonical: `${BASE_URL}/resume`,
        ogType: 'profile',
        ogImage: DEFAULT_IMAGE,
        robots: 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1',
        breadcrumbs: [
          { name: 'Home', url: `${BASE_URL}/` },
          { name: 'Resume', url: `${BASE_URL}/resume` }
        ],
        schema: {
          "@context": "https://schema.org",
          "@type": "ProfilePage",
          "name": "Girish Kumar Goyal — Resume",
          "url": `${BASE_URL}/resume`,
          "mainEntity": {
            "@type": "Person",
            "name": "Girish Kumar Goyal",
            "jobTitle": "Software Engineer",
            "url": `${BASE_URL}/resume`
          }
        }
      };

    case '/contact':
      return {
        title: 'Get in Touch — Contact | Girish Kumar Goyal',
        description: 'Contact Girish Kumar Goyal for engineering roles, software projects, or technical collaboration.',
        canonical: `${BASE_URL}/contact`,
        ogType: 'website',
        ogImage: DEFAULT_IMAGE,
        robots: 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1',
        breadcrumbs: [
          { name: 'Home', url: `${BASE_URL}/` },
          { name: 'Contact', url: `${BASE_URL}/contact` }
        ],
        schema: {
          "@context": "https://schema.org",
          "@type": "ContactPage",
          "name": "Contact Girish Kumar Goyal",
          "url": `${BASE_URL}/contact`
        }
      };

    case '/search':
      return {
        title: 'Search Portfolio Archive | Girish Kumar Goyal',
        description: 'Search across software systems, essays, bookshelf volumes, and engineering notes by Girish Kumar Goyal.',
        canonical: `${BASE_URL}/search`,
        ogType: 'website',
        ogImage: DEFAULT_IMAGE,
        robots: 'noindex, follow',
        breadcrumbs: [
          { name: 'Home', url: `${BASE_URL}/` },
          { name: 'Search', url: `${BASE_URL}/search` }
        ]
      };

    default:
      return {
        title: 'Page Not Found (404) | Girish Kumar Goyal',
        description: 'The requested catalog entry or page does not exist.',
        canonical: `${BASE_URL}/404`,
        ogType: 'website',
        ogImage: DEFAULT_IMAGE,
        robots: 'noindex, nofollow',
        breadcrumbs: [
          { name: 'Home', url: `${BASE_URL}/` },
          { name: '404', url: `${BASE_URL}/404` }
        ]
      };
  }
};

export const updatePageSEO = (pathname) => {
  const seo = getSeoMetadata(pathname);

  // 1. Update Document Title
  document.title = seo.title;

  const setMeta = (attr, key, val) => {
    let el = document.querySelector(`meta[${attr}="${key}"]`);
    if (!el) {
      el = document.createElement('meta');
      el.setAttribute(attr, key);
      document.head.appendChild(el);
    }
    el.setAttribute('content', val);
  };

  // 2. Primary Meta Tags
  setMeta('name', 'description', seo.description);
  setMeta('name', 'title', seo.title);
  setMeta('name', 'robots', seo.robots);
  setMeta('name', 'googlebot', seo.robots);

  // 3. Open Graph
  setMeta('property', 'og:title', seo.title);
  setMeta('property', 'og:description', seo.description);
  setMeta('property', 'og:url', seo.canonical);
  setMeta('property', 'og:type', seo.ogType);
  setMeta('property', 'og:image', seo.ogImage);
  setMeta('property', 'og:site_name', 'Girish Kumar Goyal');
  setMeta('property', 'og:locale', 'en_US');

  // 4. Twitter Cards
  setMeta('name', 'twitter:card', 'summary_large_image');
  setMeta('name', 'twitter:site', TWITTER_HANDLE);
  setMeta('name', 'twitter:creator', TWITTER_HANDLE);
  setMeta('name', 'twitter:title', seo.title);
  setMeta('name', 'twitter:description', seo.description);
  setMeta('name', 'twitter:url', seo.canonical);
  setMeta('name', 'twitter:image', seo.ogImage);

  // 5. Canonical Link
  let canonicalLink = document.querySelector('link[rel="canonical"]');
  if (!canonicalLink) {
    canonicalLink = document.createElement('link');
    canonicalLink.setAttribute('rel', 'canonical');
    document.head.appendChild(canonicalLink);
  }
  canonicalLink.setAttribute('href', seo.canonical);

  // 6. Dynamic Breadcrumbs JSON-LD Schema
  if (seo.breadcrumbs && seo.breadcrumbs.length > 0) {
    let bcScript = document.getElementById('route-breadcrumb-schema');
    if (!bcScript) {
      bcScript = document.createElement('script');
      bcScript.id = 'route-breadcrumb-schema';
      bcScript.type = 'application/ld+json';
      document.head.appendChild(bcScript);
    }
    const breadcrumbSchema = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": seo.breadcrumbs.map((b, idx) => ({
        "@type": "ListItem",
        "position": idx + 1,
        "name": b.name,
        "item": b.url
      }))
    };
    bcScript.textContent = JSON.stringify(breadcrumbSchema);
  }

  // 7. Dynamic Entity JSON-LD Schema
  let entityScript = document.getElementById('route-entity-schema');
  if (seo.schema) {
    if (!entityScript) {
      entityScript = document.createElement('script');
      entityScript.id = 'route-entity-schema';
      entityScript.type = 'application/ld+json';
      document.head.appendChild(entityScript);
    }
    entityScript.textContent = JSON.stringify(seo.schema);
  } else if (entityScript) {
    entityScript.remove();
  }
};

